import * as React from 'react';
import { connect } from 'react-redux';

import { GenderKeyType } from 'types/GenderKey';

import { getDisplayName } from 'components/hoc';
import { getVirtualPageTitle, getVirtualPageUrl, push as dataLayerPush } from 'helper/ga';
import appConfig from 'helper/mapBackendConfig';

import GlobalFields from 'defs/ga/fields/global';
import VirtualPageViewFields from 'defs/ga/fields/virtualPageView';
import { trackVirtualPageView } from 'defs/ga/track/virtualPageView';

import { getIsAuthenticated } from 'store/login';
import { getGender } from 'store/profile';
import { getCurrentViewID } from 'store/view';

type VirtualPageViewProxyProps = {
	currentViewID: string;
	gender: GenderKeyType;
	isAuthenticated: boolean;
};

type VirtualPageViewProxyState = {};

type VirtualPageViewTrackingData = {
	[key: string]: any;
};

export type WithVirtualPageView = {
	getVirtualPageViewObject: (params?: VirtualPageViewTrackingData) => VirtualPageViewTrackingData;
	trackVirtualPageView: (
		params?: VirtualPageViewTrackingData,
		delay?: number,
		lastTrackedViewComparison?: boolean
	) => void;
};

/*
 * @version 1.0.0
 * @author Jan Biasi <biasijan@gmail.com>
 * @param {React.Component} WrappedComponent
 */
export default function withVirtualPageView<P extends object>(WrappedComponent: React.ComponentType<P>) {
	class VirtualPageViewProxy extends React.Component<P & VirtualPageViewProxyProps, VirtualPageViewProxyState> {
		public static displayName = `VirtualPageView(${getDisplayName(WrappedComponent)})`;

		public lastTrackedViewID: string = '';

		public getVirtualPageViewObject(params: VirtualPageViewTrackingData = {}): VirtualPageViewTrackingData {
			let virtualPageUrl = getVirtualPageUrl();
			const currentVirtualPageTitle = getVirtualPageTitle();
			const { currentViewID, gender, isAuthenticated } = this.props;
			const { pageType, contentType, hrco, virtualPageTitle } = params;

			virtualPageUrl = virtualPageUrl.length === 0 ? '/' : virtualPageUrl;

			return trackVirtualPageView({
				...params,
				[GlobalFields.VirtualPageURL]: virtualPageUrl,
				[GlobalFields.VirtualPageTitle]: virtualPageTitle || currentVirtualPageTitle || '',
				[VirtualPageViewFields.PageType]: pageType || 'coach',
				[VirtualPageViewFields.ContentType]: contentType || 'coach',
				[VirtualPageViewFields.PageName]: virtualPageTitle,
				[VirtualPageViewFields.PageId]: currentViewID,
				[VirtualPageViewFields.PageGroupCategory]:
					virtualPageUrl.split('/').filter((path: string) => Boolean(path.length > 0)) || [],
				[VirtualPageViewFields.Language]: appConfig.env.language,
				[VirtualPageViewFields.EnvironementId]: appConfig.env.id || 'production',
				[VirtualPageViewFields.HTTPResponseCode]: hrco || 200,
				[VirtualPageViewFields.Client]: 'impuls',
				[VirtualPageViewFields.VisitorLoginState]: !!isAuthenticated,
				[VirtualPageViewFields.Sex]: gender ? (gender.toLowerCase() === 'f' ? 'female' : 'male') : '',
			} as any);
		}

		public internalVirtualPageViewTracker(
			params: VirtualPageViewTrackingData = {},
			delay = 1,
			lastTrackedViewComparison = true
		) {
			const tid = setTimeout((): any => {
				const virtualPageViewObject = this.getVirtualPageViewObject(params);

				if (
					lastTrackedViewComparison &&
					this.lastTrackedViewID === virtualPageViewObject[VirtualPageViewFields.PageId]
				) {
					return false;
				}

				this.lastTrackedViewID = virtualPageViewObject[VirtualPageViewFields.PageId];
				dataLayerPush(virtualPageViewObject);
				clearTimeout(tid);
			}, delay || 1);
		}

		public render() {
			return (
				<WrappedComponent
					// tslint:disable-next-line: jsx-no-bind
					getVirtualPageViewObject={this.getVirtualPageViewObject.bind(this)}
					// tslint:disable-next-line: jsx-no-bind
					trackVirtualPageView={this.internalVirtualPageViewTracker.bind(this)}
					{...this.props}
				/>
			);
		}
	}

	const mapStateToProps = (state) => ({
		currentViewID: getCurrentViewID(state),
		gender: getGender(state),
		isAuthenticated: getIsAuthenticated(state),
	});

	return connect(mapStateToProps)(VirtualPageViewProxy as any);
}
